/**
 * SignalExplainer — plain-English "why is this run blocked" panel.
 *
 * Renders the four self-scoring signals produced by `backend/live_signals.py`:
 *   - base confidence (regime-conditional hit-rate or run-wide fallback)
 *   - alignment votes (forecast bias, path direction, VIX drift, OR breakout)
 *   - or_break_with_breadth (C1's alt-escape)
 *   - rarity_ok (B1 gate)
 * plus a short "what's missing to unblock" hint pointing at C1/C2/B2.
 *
 * Fetches from `/v1/sessions/{date}/live-signals` which returns the freshest
 * (RECALC-or-original) row per run, so operators always see the latest reading.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ruleMeaning } from "@/lib/ruleMeanings";
import { Icon, Panel, RuleResult } from "@/sentinel";

/**
 * What would clear each rule, in plain words. The rule's meaning itself comes from
 * lib/ruleMeanings (the same sentences the backend uses); these lines say what has to change.
 */
export const RULE_HINTS = {
  C1: "Clears when two runs in a row read the same price path, or when the market breaks out of its opening range with most stocks moving the same way.",
  C2: "Clears when at least 3 of the 4 live market readings (price path, VIX drift, opening-range break, breadth) point the same way.",
  C3: "Clears when rates, oil, VIX and gold are not making an unusually large or conflicting move. The limits widen when the market is more volatile.",
  EDGE: "Clears when today's setup matches every saved entry condition. Old, missing or unsupported evidence keeps it blocked.",
  E1: "Clears when the best candidate's expected payoff, after costs, is at least 8% of the most it can lose (12% on a high-volatility day).",
  E2: "Clears when the possible reward is large enough against the possible loss: at least 1.2 times for a debit spread, or a credit of at least 20% of the width.",
  E3: "Clears when the model's chance of profit reaches the minimum for this kind of spread: 40% for a debit, 65% for a credit (70% on a high-volatility day).",
  L1: "Clears when the gap between the bid and ask on each leg is no more than 10% of the spread width and no more than $0.60.",
  L2: "Clears when both legs have fresh, valid prices from today's session.",
  L3: "Clears when the short strike shows enough trading: at least 500 open contracts or 200 traded today.",
  L4: "Clears when both strikes are within 3% of the index level.",
  T1: "Clears when the chance of the index touching the short strike is 35% or less (credit spreads only).",
  T2: "Clears when the economic calendar is verified and no high- or medium-impact US release falls within 30 minutes.",
  "DATA-01": "Clears when both legs have real, same-day option prices no older than 60 seconds. Missing, modelled or crossed prices cannot allow entry.",
  "KS-01": "Clears when the missing market input is back and verified.",
  "KS-02": "Clears when the decision date, the contract expiry and the timezone all match the session.",
  "KS-06": "Clears once the pending high-impact release is out and the forecast is refreshed.",
  "KS-07": "Clears when an owner reviews the account-risk limit that was reached.",
  KILL: "Clears when an owner disarms the kill switch in Admin.",
  T3: "Short-term volatility (VIX1D) rose more than 15%. The payoff and chance-of-profit floors go up until it settles.",
  B1: "Clears when confidence is at least 90 and this kind of day has paid off at least 60% of the time over 40 or more past trades.",
  B2: "Clears when confidence is at least 65 and every liquidity, payoff, risk and confirmation rule passes.",
};

// Direction is price-direction language: blue ▲ / orange ▼ text only, never a fill.
const dirOf = (d) => (d === "up" || (d && d.startsWith("up")) ? "up" : d === "dn" || (d && d.startsWith("dn")) ? "down" : "flat");
const ARROW = { up: "▲", down: "▼", flat: "◆" };

function VotePill({ id, label, direction, note }) {
  const dir = direction ? dirOf(direction) : null;
  return (
    <div className="sn-stat sn-stat-sm" data-testid={`signal-vote-${id}`}
      style={{ padding: "var(--space-2) var(--space-3)", border: "1px solid var(--border-hairline)", borderRadius: "var(--radius-sm)", background: "var(--surface-sunken)", overflowWrap: "anywhere" }}>
      <span className="sn-stat-k">{label}</span>
      <span className={dir ? `sn-delta sn-price-${dir}` : "sn-faint"} style={{ fontSize: 13 }}>
        {dir && <span className="sn-arrow" aria-hidden="true">{ARROW[dir]}</span>}
        <span className="sn-num">{note || "—"}</span>
      </span>
    </div>
  );
}

const Tile = ({ children, title, testId }) => (
  <div className="sn-small sn-muted" title={title} data-testid={testId}
    style={{ padding: "var(--space-2) var(--space-3)", border: "1px solid var(--border-hairline)", borderRadius: "var(--radius-sm)", minWidth: 0, overflowWrap: "anywhere" }}>
    {children}
  </div>
);

export function SignalExplainer({ date, run, live = false }) {
  const { data } = useQuery({
    queryKey: ["live-signals", date],
    queryFn: () => api.getLiveSignals(date),
    enabled: !!date,
    refetchInterval: live ? 20000 : false,
  });
  const checkpoint = run != null ? (data?.checkpoints || []).find((c) => c.run === run)
                                  : (data?.checkpoints || []).slice(-1)[0];
  const ls = checkpoint?.live_signals;
  if (!ls || !ls.regime_tags) {
    return (
      <Panel title="Signal explainer" icon="Activity" data-testid="signal-explainer-empty">
        <p className="sn-small sn-faint" style={{ margin: 0 }}>
          No live-signal diagnostics were recorded for this checkpoint.
        </p>
      </Panel>
    );
  }
  const detail = ls.alignment_detail || {};
  const conf = checkpoint.confidence ?? 0;
  const block = checkpoint.blocking_rule;
  const hint = block ? RULE_HINTS[block] : null;
  const dir = ls.direction;
  const dirLabel = dir === "up" ? "BULLISH" : dir === "dn" ? "BEARISH" : "MIXED";
  const dirKey = dir === "up" ? "up" : dir === "dn" ? "down" : "flat";
  const aligned = (ls.alignment_count ?? 0) >= 3;

  return (
    <Panel title="Signal explainer · why this run is where it is" icon="Activity" data-testid="signal-explainer"
      meta={<span className={`sn-delta sn-price-${dirKey}`} style={{ fontSize: 12, fontWeight: 600 }} data-testid="signal-direction"><span className="sn-arrow" aria-hidden="true">{ARROW[dirKey]}</span>{dirLabel}</span>}>
      <div className="sn-stack">
        <p className="sn-muted" style={{ margin: 0, fontSize: 13, overflowWrap: "anywhere" }} data-testid="signal-confidence-evidence">
          {ls.conf_note} · boost <b className="sn-num" style={{ color: "var(--text-primary)", fontWeight: 500 }}>+{ls.boost ?? 0}</b> → confidence <b className="sn-num" style={{ color: "var(--text-primary)", fontWeight: 500 }}>{conf}</b>.
        </p>

        <p className="sn-small" data-testid="signal-calibration-source"
          style={{ margin: 0, overflowWrap: "anywhere", color: ls.calibration_available ? "var(--text-secondary)" : "var(--provenance)" }}>
          {!ls.calibration_available && <Icon name="Hourglass" size={12} style={{ marginRight: 4 }} />}
          {ls.calibration ? <>Approved history <span className="sn-num">{ls.calibration.start} → {ls.calibration.end}</span> · n=<span className="sn-num">{ls.historical_n ?? "—"}</span> used · {ls.regime_version}</> : "No approved calibration recorded. Review live confidence evidence in Admin; previous checkpoints remain unchanged."}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--space-2)" }} data-testid="signal-votes">
          <VotePill id="forecast" label="Forecast bias" direction={detail.forecast_bias} note={detail.forecast_bias} />
          <VotePill id="path" label="Path" direction={detail.path} note={detail.path} />
          <VotePill id="vix" label="VIX drift" direction={detail.vix} note={detail.vix} />
          <VotePill id="or" label="OR break" direction={detail.or_break} note={detail.or_break} />
          <VotePill id="breadth" label="Breadth" direction={detail.breadth} note={detail.breadth} />
        </div>
        <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="signal-forecast-vote-note">{ls.forecast_vote_note || "Five sources are reported. A symmetric forecast contributes no directional vote; path, VIX drift, opening-range break and breadth supply the four effective live votes."}</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--space-2)" }}>
          <Tile>
            Alignment: <RuleResult result={aligned ? "PASS" : "FAIL"} data-testid="signal-alignment-count">{ls.alignment_count ?? 0} votes agree</RuleResult>
            <span className="sn-faint"> (C2 needs ≥ 3)</span>
          </Tile>
          <Tile>
            OR break with breadth: <b style={{ color: "var(--text-primary)", fontWeight: 600 }}>{ls.or_break_with_breadth ? "YES" : "NO"}</b>
            <span className="sn-faint"> (C1 alternative)</span>
          </Tile>
          <Tile title={ls.rarity_note}>
            Rarity gate: <RuleResult result={ls.rarity_ok ? "PASS" : "FAIL"} />
            <span className="sn-faint"> (B1 · {ls.rarity_note})</span>
          </Tile>
        </div>

        {block && (
          <div className="sn-row" data-testid="signal-hint"
            style={{ alignItems: "flex-start", flexWrap: "nowrap", padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-sm)",
              background: "var(--surface-sunken)", boxShadow: "inset 3px 0 0 var(--decision-no-trade-accent)", fontSize: 13 }}>
            <span style={{ minWidth: 0 }}>
              <b style={{ fontWeight: 600 }}>{ruleMeaning(block) || `Blocked on ${block}`}</b>
              <span className="sn-small sn-faint"> {block}</span>{" "}
              <span className="sn-muted">{hint || "See the Thresholds page for this rule's limit."}</span>
            </span>
          </div>
        )}
      </div>
    </Panel>
  );
}
